import { useState } from "react";
import styles from "./UserAdministrationUser.module.scss";
import { DefaultSection } from "../../../../common/components/common/DefaultSection";
import {
  getAvatarGradient,
  getInitials,
} from "../../../portal/myProfile/components/ProfileAvatar";
import {
  constructFamilyHref,
  constructUserBodyMetricsHref,
  constructUserGoalsHref,
  constructUserWorkoutsHref,
} from "../../../../common/infrastructure/routes/routePaths";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { SpecificPageNotFound } from "../../../../common/components/errorPages/SpecificPageNotFound";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../../../common/infrastructure/components/RenderWhen";
import { SectionInfoRow } from "../../../../common/components/common/SectionInfoRow";
import { AppLink } from "../../../../common/components/button/link/Link";
import { PlanRow } from "../../../../common/components/common/PlanRow/PlanRow";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { EventHistoryItem } from "../../../../common/components/common/eventHistoryItem/EventHistoryItem";
import { useUserAdministrationUser } from "./hooks/useUserAdministrationUser";
import { UserAdditionalDetailsDialog } from "./components/UserAdditionalDetailsDialog";
import { BulkDeleteUserEventsDialog } from "./components/BulkDeleteUserEventsDialog";
import { UserDepositDialog } from "./components/UserDepositDialog";
import { UserFormsDialog } from "./components/UserFormsDialog";
import { MergeUsersDialog } from "./components/MergeUsersDialog";
import { RenewCardDialog } from "./components/RenewCardDialog/RenewCardDialog";
import { HistoryDialog } from "../../../../common/components/history/HistoryDialog";
import { USER_HISTORY_CONFIG } from "../../../../common/utilities/userAuditLogUtility";

export const UserAdministrationUser = () => {
  const {
    user,
    isLoading,
    isStoreOnly,
    showPlans,
    showBodyMetrics,
    showWorkouts,
    showGoals,
    showForms,
    tenantSettings,
    listActionButtons,
    showPlansPopup,
    showAdditionalDetailsPopup,
    showBulkDeletePopup,
    showDepositPopup,
    showFormsPopup,
    showHistoryPopup,
    showMergePopup,
    showRenewCardPopup,
    setShowPlansPopup,
    setShowAdditionalDetailsPopup,
    setShowBulkDeletePopup,
    setShowDepositPopup,
    setShowFormsPopup,
    setShowHistoryPopup,
    setShowMergePopup,
    setShowRenewCardPopup,
    depositBalanceText,
    depositBalanceTone,
    backToHome,
    generateEmailComponent,
    generatePhoneNumberComponent,
    navigate,
    onEventHistoryItemClick,
  } = useUserAdministrationUser();

  const [showPhotoDialog, setShowPhotoDialog] = useState(false);

  if (!user && !isLoading) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такъв потребител"}
        buttonText={"Обратно към начало"}
        onButtonClick={backToHome}
      />
    );
  }

  return (
    <div className={styles.root}>
      <DefaultSection>
        <div className={styles.root_header_row}>
          {isLoading ? (
            <div className={styles.root_header_avatar_skeleton} />
          ) : user && (
            <div
              className={`${styles.root_header_avatar}${user.profilePhotoUrl ? ` ${styles.root_header_avatar_clickable}` : ""}`}
              style={
                !user.profilePhotoUrl
                  ? { background: getAvatarGradient(user.id) }
                  : undefined
              }
              onClick={() => { if (user.profilePhotoUrl) setShowPhotoDialog(true); }}
            >
              {user.profilePhotoUrl ? (
                <img
                  src={user.profilePhotoUrl}
                  alt=""
                  className={styles.root_header_avatar_img}
                />
              ) : (
                <span className={styles.root_header_avatar_initials}>
                  {getInitials(user.firstName, user.lastName)}
                </span>
              )}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={styles.root_header_title}>
              <SkeletonItem
                width={"80%"}
                isLoading={isLoading}
                value={
                  <>
                    {user?.firstName} {user?.lastName}{" "}
                    <span className={styles.root_header_title_id}>
                      (#{user?.id})
                    </span>
                  </>
                }
              />
            </div>
            <div className={styles.root_header_email}>
              <SkeletonItem
                width={"40%"}
                isLoading={isLoading}
                value={generateEmailComponent()}
              />
            </div>
          </div>
        </div>
      </DefaultSection>

      <RenderWhen condition={!!user && !!user?.isUnreliable}>
        <DefaultSection className={styles.root_info_section}>
          <div className={styles.root_info_section_unreliable}>
            НЕРЕДОВЕН КЛИЕНТ
          </div>
        </DefaultSection>
      </RenderWhen>

      <DefaultSection className={styles.root_info_section}>
        <RenderWhen condition={!!user?.additionalInfo}>
          <SectionInfoRow
            label={"Допълнителна информация"}
            data={user?.additionalInfo}
            isLoading={isLoading}
            skekletonWidth={200}
          />
        </RenderWhen>
        <SectionInfoRow
          label={"Телефонен номер"}
          data={generatePhoneNumberComponent()}
          isLoading={isLoading}
          skekletonWidth={200}
        />

        <RenderWhen condition={!!tenantSettings?.isMultisportAvailable}>
          <SectionInfoRow
            label={"Използвал ли е Multisport"}
            data={user?.hasUsedMultisportBefore ? "✓" : "X"}
            isLoading={isLoading}
            skekletonWidth={50}
          />
        </RenderWhen>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        {/* Family and plans are irrelevant for a shop-only studio. */}
        <RenderWhen condition={!isStoreOnly}>
          <SectionInfoRow
            label={"Семейство"}
            data={
              <RenderWhen condition={!!user?.familyId}>
                <IfTrue>
                  <AppLink
                    className={styles.root_info_section_link}
                    onClick={() =>
                      navigate(constructFamilyHref(user?.familyId ?? 0))
                    }
                    label={`${user?.familyName} (#${user?.familyId})`}
                  />
                </IfTrue>
                <IfFalse>
                  N/A
                </IfFalse>
              </RenderWhen>
            }
            isLoading={isLoading}
            skekletonWidth={400}
          />
        </RenderWhen>

        <RenderWhen condition={showPlans}>
          <SectionInfoRow
            label={"Планове"}
            skekletonWidth={100}
            data={
              <span data-onboarding="user-plans">
                <AppLink
                  className={styles.root_info_section_link}
                  onClick={() => setShowPlansPopup(true)}
                  label={"виж >"}
                />
              </span>
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={!isStoreOnly}>
          <SectionInfoRow
            label={
              <>
                Депозити{" "}
                <span className={styles.root_info_section_deposit_amount}>
                  (
                  <span
                    className={
                      styles[
                        `root_info_section_deposit_balance_${depositBalanceTone}`
                      ]
                    }
                  >
                    {depositBalanceText}
                  </span>
                  )
                </span>
                :
              </>
            }
            skekletonWidth={100}
            data={
              <span data-onboarding="user-deposit">
                <AppLink
                  className={styles.root_info_section_link}
                  onClick={() => setShowDepositPopup(true)}
                  label={"виж >"}
                />
              </span>
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <SectionInfoRow
          label={"Допълнителни детайли"}
          skekletonWidth={100}
          data={
            <span data-onboarding="user-additional-details">
              <AppLink
                className={styles.root_info_section_link}
                onClick={() => setShowAdditionalDetailsPopup(true)}
                label={"виж >"}
              />
            </span>
          }
          isLoading={isLoading}
        />

        <RenderWhen condition={showWorkouts}>
          <SectionInfoRow
            label={"Тренировки"}
            skekletonWidth={100}
            data={
              <span data-onboarding="user-workouts">
                <AppLink
                  className={styles.root_info_section_link}
                  onClick={() =>
                    navigate(constructUserWorkoutsHref(user?.id ?? 0))
                  }
                  label={"виж >"}
                />
              </span>
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={showBodyMetrics}>
          <SectionInfoRow
            label={"Метрики"}
            skekletonWidth={100}
            data={
              <AppLink
                className={styles.root_info_section_link}
                onClick={() =>
                  navigate(constructUserBodyMetricsHref(user?.id ?? 0))
                }
                label={"виж >"}
              />
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={showGoals}>
          <SectionInfoRow
            label={"Цели"}
            skekletonWidth={100}
            data={
              <AppLink
                className={styles.root_info_section_link}
                onClick={() => navigate(constructUserGoalsHref(user?.id ?? 0))}
                label={"виж >"}
              />
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={showForms}>
          <SectionInfoRow
            label={"Форми"}
            skekletonWidth={100}
            data={
              <AppLink
                className={styles.root_info_section_link}
                onClick={() => setShowFormsPopup(true)}
                label={"виж >"}
              />
            }
            isLoading={isLoading}
          />
        </RenderWhen>
      </DefaultSection>

      <ItemsList
        isLoading={isLoading}
        noItemsText="Не беше открита история."
        className={styles.list}
        title="История"
        actionButton={listActionButtons}
        itemTemplate={(item, index, actionButtons) => {
          return (
            <EventHistoryItem
              event={item}
              key={item?.id ?? index}
              isLoading={isLoading}
              actionButtons={actionButtons}
              onEventClick={onEventHistoryItemClick}
            />
          );
        }}
        items={user?.history ?? []}
        fullHeight
        data-onboarding="user-event-history"
      />

      <AppDialog
        header="История на плановете"
        visible={showPlansPopup}
        className={styles.root_plans_dialog}
        onHide={() => setShowPlansPopup(false)}
      >
        <div className={styles.root_plans_dialog_content_wrapper}>
          <RenderWhen condition={user?.plans.length === 0}>
            <IfTrue>
              <>Няма открити резултати.</>
            </IfTrue>
            <IfFalse>
              {user?.plans.map((plan, i) => (
                <PlanRow key={`${plan.id}_${i}`} plan={plan} />
              ))}
            </IfFalse>
          </RenderWhen>
        </div>
      </AppDialog>

      <UserAdditionalDetailsDialog
        visible={showAdditionalDetailsPopup}
        onHide={() => setShowAdditionalDetailsPopup(false)}
        user={user}
      />

      <BulkDeleteUserEventsDialog
        visible={showBulkDeletePopup}
        onHide={() => setShowBulkDeletePopup(false)}
        userId={user?.id}
        history={user?.history}
      />

      <MergeUsersDialog
        visible={showMergePopup}
        onHide={() => setShowMergePopup(false)}
        userId={user?.id}
      />

      <RenewCardDialog
        visible={showRenewCardPopup}
        onHide={() => setShowRenewCardPopup(false)}
        userId={user?.id}
      />

      {user && (
        <HistoryDialog
          entityId={user.id}
          entityCreatedOn={user.createdOn}
          timezone={tenantSettings?.timezone}
          showDialog={showHistoryPopup}
          onHide={() => setShowHistoryPopup(false)}
          label="История"
          config={USER_HISTORY_CONFIG}
        />
      )}

      <UserDepositDialog
        visible={showDepositPopup}
        onHide={() => setShowDepositPopup(false)}
        user={user}
      />

      <UserFormsDialog
        visible={showFormsPopup}
        onHide={() => setShowFormsPopup(false)}
        user={user}
      />

      <AppDialog
        header="Профилна снимка"
        visible={showPhotoDialog}
        className={styles.root_photo_dialog}
        onHide={() => setShowPhotoDialog(false)}
      >
        {user?.profilePhotoUrl && (
          <img
            src={user.profilePhotoUrl}
            alt="Профилна снимка"
            className={styles.root_photo_dialog_img}
          />
        )}
      </AppDialog>

    </div>
  );
};
